import React from 'react';
import {
  BookOpen,
  Search,
  PlusCircle,
  CheckCircle,
  Activity,
  MessageCircle,
  Info,
  AlertCircle,
  FileText
} from 'lucide-react';

export default function HelpCenter() {
  return (
    <div className="w-full min-h-full bg-white text-slate-900 pb-20">
      {/* Header */}
      <div className="border-b border-border bg-slate-50 border-slate-200/60">
        <div className="max-w-7xl mx-auto px-6 py-12 md:py-16">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-primary/10 rounded-lg text-primary">
              <BookOpen size={24} />
            </div>
            <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Help Center & Documentation</h1>
          </div>
          <p className="text-lg text-slate-600 max-w-2xl leading-relaxed">
            Everything you need to navigate the Univdiam App, create orders, review CAD designs, and communicate with your team.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12 grid grid-cols-1 md:grid-cols-[240px_1fr] gap-12 items-start relative">

        {/* Sticky Sidebar Navigation */}
        <div className="hidden md:block sticky top-8">
          <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wider mb-4">On This Page</h3>
          <nav className="flex flex-col space-y-3 border-l border-slate-200">
            <a href="#welcome" className="text-base text-slate-600 hover:text-primary transition-colors border-l-2 border-transparent hover:border-primary pl-4 -ml-[1px]">Welcome</a>
            <a href="#finding-products" className="text-base text-slate-600 hover:text-primary transition-colors border-l-2 border-transparent hover:border-primary pl-4 -ml-[1px]">Finding Products</a>
            <a href="#creating-order" className="text-base text-slate-600 hover:text-primary transition-colors border-l-2 border-transparent hover:border-primary pl-4 -ml-[1px]">Creating an Order</a>
            <a href="#cad-review" className="text-base text-slate-600 hover:text-primary transition-colors border-l-2 border-transparent hover:border-primary pl-4 -ml-[1px]">CAD Review & Approval</a>
            <a href="#tracking-orders" className="text-base text-slate-600 hover:text-primary transition-colors border-l-2 border-transparent hover:border-primary pl-4 -ml-[1px]">Tracking Orders</a>
            <a href="#chat-support" className="text-base text-slate-600 hover:text-primary transition-colors border-l-2 border-transparent hover:border-primary pl-4 -ml-[1px]">Live Chat & Support</a>
          </nav>
        </div>

        {/* Main Content */}
        <div className="space-y-16">

          {/* Section 1 */}
          <section id="welcome" className="scroll-mt-8 border-b border-border pb-12">
            <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2">
              <FileText className="text-slate-400" size={20} />
              Welcome to the App
            </h2>
            <div className="prose prose-slate max-w-none">
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                Your primary workspace is the Dashboard. Here you will find an overview of your active projects, recent orders, fast navigation links to the catalog, and recent notifications regarding your products.
              </p>
              <figure className="my-8 rounded-xl overflow-hidden border border-border shadow-sm bg-slate-50">
                <img src="/onboarding/dashboard.png" alt="Dashboard Overview" className="w-full object-cover" />
                <figcaption className="text-sm text-center text-slate-500 py-3 bg-white border-t border-border">
                  The main dashboard view showing active projects and quick actions.
                </figcaption>
              </figure>
            </div>

            <div className="bg-blue-50/50 border border-blue-100 rounded-lg p-4 mt-6 flex gap-3 text-blue-900">
              <Info className="flex-shrink-0 text-blue-600 mt-0.5" size={20} />
              <div className="text-base leading-relaxed">
                <strong>Pro Tip:</strong> Click your profile icon in the bottom-left corner of the sidebar to access your personal settings and manage layout preferences.
              </div>
            </div>
          </section>

          {/* Section 2 */}
          <section id="finding-products" className="scroll-mt-8 border-b border-border pb-12">
            <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2">
              <Search className="text-slate-400" size={20} />
              Finding What You Need
            </h2>
            <div className="prose prose-slate max-w-none">
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                The Catalog is where you explore existing products. Use the universal search bar at the top or filter by specific groups (categories, sub-categories, or collections) in the sidebar catalog view.
              </p>
              <figure className="my-8 rounded-xl overflow-hidden border border-border shadow-sm bg-slate-50">
                <img src="/onboarding/catalog.png" alt="Catalog Browsing" className="w-full object-cover" />
                <figcaption className="text-sm text-center text-slate-500 py-3 bg-white border-t border-border">
                  Browsing the catalog with categories and advanced filters.
                </figcaption>
              </figure>
            </div>
          </section>

          {/* Section 3 */}
          <section id="creating-order" className="scroll-mt-8 border-b border-border pb-12">
            <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2">
              <PlusCircle className="text-slate-400" size={20} />
              Creating Your First Custom Order
            </h2>

            <ol className="space-y-6 list-decimal pl-5 text-lg text-slate-600 leading-relaxed mb-8 marker:text-slate-400 marker:font-medium">
              <li className="pl-2">
                <strong className="text-slate-900 font-semibold block">Click "Create Order"</strong>
                <span className="block mt-1">Locate the "Create a Custom Order" button at the bottom of the left sidebar.</span>
              </li>
              <li className="pl-2">
                <strong className="text-slate-900 font-semibold block">Fill Details</strong>
                <span className="block mt-1">Enter a clear job title, add reference images or sketches, and specify details regarding material, gemstone type, weight, and size.</span>
              </li>
              <li className="pl-2">
                <strong className="text-slate-900 font-semibold block">Submit</strong>
                <span className="block mt-1">Click submit. A dedicated order chat channel will automatically be generated in the "All Chats" section.</span>
              </li>
            </ol>

            <figure className="my-8 rounded-xl overflow-hidden border border-border shadow-sm bg-slate-50">
              <img src="/onboarding/create_order.png" alt="Creating an Order" className="w-full object-cover" />
            </figure>
          </section>

          {/* Section 4 */}
          <section id="cad-review" className="scroll-mt-8 border-b border-border pb-12">
            <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2">
              <CheckCircle className="text-slate-400" size={20} />
              CAD Review & Approval
            </h2>
            <div className="prose prose-slate max-w-none">
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                When a CAD designer completes the initial 3D model, you will receive a notification. You can inspect the CAD in detail directly from the Order view.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              <div className="bg-slate-50 p-5 rounded-lg border border-border">
                <h4 className="font-semibold text-slate-900 mb-2">Request Revisions</h4>
                <p className="text-base text-slate-600">If changes are needed, you can use share an update chat panel to request changes.</p>
              </div>
              <div className="bg-slate-50 p-5 rounded-lg border border-border">
                <h4 className="font-semibold text-slate-900 mb-2">Submit Order Request</h4>
                <p className="text-base text-slate-600">If the 3D model is perfect, click "Submit Order Request". We will process your order to manufacturing.</p>
              </div>
            </div>

            <figure className="my-8 rounded-xl overflow-hidden border border-border bg-slate-50">
              <img src="/onboarding/cad.png" alt="CAD Review" className="w-full object-cover border-b border-border" />
            </figure>

            <div className="bg-amber-50/50 border border-amber-100 rounded-lg p-4 mt-6 flex gap-3 text-amber-900">
              <AlertCircle className="flex-shrink-0 text-amber-500 mt-0.5" size={20} />
              <div className="text-base leading-relaxed">
                <strong>Attention:</strong> Once a CAD is approved and moves to Manufacturing, further structural changes cannot be made without halting production and recalculating costs.
              </div>
            </div>
          </section>

          {/* Section 5 */}
          <section id="tracking-orders" className="scroll-mt-8 border-b border-border pb-12">
            <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2">
              <Activity className="text-slate-400" size={20} />
              Tracking Orders
            </h2>
            <div className="prose prose-slate max-w-none">
              <p className="text-lg text-slate-600 leading-relaxed">
                The Kanban board on the Dashboard allows you to visually track orders through the production pipeline: <strong className="font-medium text-slate-900">Under Univ Diam Review &rarr; CAD/Quote in Progress (NEW / REVISION) &rarr; Waiting for Client Approval &rarr; In Manufacturing &rarr; Ready at MFR Facility &rarr; In Transit to Univ Diam USA &rarr; Ready to Deliver &rarr; Completed</strong>.
              </p>
            </div>
          </section>

          {/* Section 6 */}
          <section id="chat-support" className="scroll-mt-8 pb-12">
            <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2">
              <MessageCircle className="text-slate-400" size={20} />
              Live Chat & Support
            </h2>
            <div className="prose prose-slate max-w-none mb-6">
              <p className="text-lg text-slate-600 leading-relaxed">
                Every order has an isolated chat channel to keep communication organized and context-specific.
              </p>
            </div>

            <ul className="space-y-4 mb-8">
              <li className="flex items-start gap-3">
                <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-slate-300 flex-shrink-0" />
                <p className="text-lg text-slate-600"><strong className="text-slate-900 font-medium">All Chats:</strong> Access the "All Chats" section from the sidebar to view all active conversations natively.</p>
              </li>
              <li className="flex items-start gap-3">
                <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-slate-300 flex-shrink-0" />
                <p className="text-lg text-slate-600"><strong className="text-slate-900 font-medium">Inside an Order:</strong> When viewing an Order Detail page, click the chat bubble icon in the top right to open the slide-over chat interface immediately.</p>
              </li>
            </ul>

            <figure className="my-8 rounded-xl overflow-hidden border border-border shadow-sm bg-slate-50">
              <img src="/onboarding/chat.png" alt="Chat Interface" className="w-full object-cover" />
            </figure>
          </section>

        </div>
      </div>
    </div>
  );
}
